import {TCStringDecoder} from "./tc-string-decoder";
import {decodeBase64BigEndian} from "../../util/binary";
import {log} from "../../util/log";

interface ICondition {
    field:string;
    value:any;
}

interface IGenericField {
    name:string;
    condition?:ICondition;
}

interface IField extends IGenericField {
    bits:string;
    type:string;
}
interface IFieldArray extends IGenericField {
    iterations:string;
    fields:IField[];
}

interface ITCStringFields {
    core:(IField|IFieldArray)[]
}

class FieldDefTCStringDecoder extends TCStringDecoder {

    private static LETTERS:string = 'abcdefghijklmnopqrstuvwxyz';

    private static fields:ITCStringFields = {
        core: [
            {
                name: 'version',
                bits: '6',
                type: 'number'
            },
            {
                name: 'created',
                bits: '36',
                type: 'number'
            },
            {
                name: 'lastUpdated',
                bits: '36',
                type: 'number'
            },
            {
                name: 'cmpId',
                bits: '12',
                type: 'number'
            },
            {
                name: 'cmpVersion',
                bits: '12',
                type: 'number'
            },
            {
                name: 'consentScreen',
                bits: '6',
                type: 'number'
            },
            {
                name: 'consentLanguage',
                bits: '12',
                type: 'string'
            },
            {
                name: 'vendorListVersion',
                bits: '12',
                type: 'number'
            },
            {
                name: 'tcfPolicyVersion',
                bits: '6',
                type: 'number'
            },
            {
                name: 'isServiceSpecific',
                bits: '1',
                type: 'boolean'
            },
            {
                name: 'useNonStandardStacks',
                bits: '1',
                type: 'boolean'
            },
            {
                name: 'specialFeatureOptIns',
                bits: '12',
                type: 'bitmap'
            },
            {
                name: 'purposesConsent',
                bits: '24',
                type: 'bitmap'
            },
            {
                name: 'purposesLITransparency',
                bits: '24',
                type: 'bitmap'
            },
            {
                name: 'specificJurisdictionDisclosures.purposeOneTreatment',
                bits: '1',
                type: 'boolean'
            },
            {
                name: 'specificJurisdictionDisclosures.publisherCC',
                bits: '12',
                type: 'string'
            },
            {
                name: 'vendorConsentSection.maxVendorId',
                bits: '16',
                type: 'number'
            },
            {
                name: 'vendorConsentSection.isRangeEnconding',
                bits: '1',
                type: 'boolean'
            },
            {
                name: 'vendorConsentSection.bitFieldSection.bitField',
                bits: '#vendorConsentSection.maxVendorId',
                type: 'bitmap',
                condition: {
                    field: 'vendorConsentSection.isRangeEnconding',
                    value: false
                }
            },
            {
                name: 'vendorConsentSection.rangeSection.numEntries',
                bits: '12',
                type: 'number',
                condition: {
                    field: 'vendorConsentSection.isRangeEnconding',
                    value: true
                }
            },
            {
                iterations: '#vendorConsentSection.rangeSection.numEntries',
                name: 'vendorConsentSection.rangeSection.rangeEntries',
                fields: [
                    {
                        name: 'isARange',
                        bits: '1',
                        type: 'boolean'
                    },
                    {
                        name: 'startOrOnlyVendorId',
                        bits: '16',
                        type: 'number'
                    },
                    {
                        name: 'endVendorId',
                        bits: '16',
                        type: 'number',
                        condition: {
                            field: 'isARange',
                            value: true
                        }
                    }
                ],
                condition: {
                    field: 'vendorConsentSection.isRangeEnconding',
                    value: true
                }
            },
            {
                name: 'vendorLegitimateInterestSection.maxVendorId',
                bits: '16',
                type: 'number'
            },
            {
                name: 'vendorLegitimateInterestSection.isRangeEnconding',
                bits: '1',
                type: 'boolean'
            },
            {
                name: 'vendorLegitimateInterestSection.bitFieldSection.bitField',
                bits: '#vendorLegitimateInterestSection.maxVendorId',
                type: 'bitmap',
                condition: {
                    field: 'vendorLegitimateInterestSection.isRangeEnconding',
                    value: false
                }
            },
            {
                name: 'vendorLegitimateInterestSection.rangeSection.numEntries',
                bits: '12',
                type: 'number',
                condition: {
                    field: 'vendorLegitimateInterestSection.isRangeEnconding',
                    value: true
                }
            },
            {
                iterations: '#vendorLegitimateInterestSection.rangeSection.numEntries',
                name: 'vendorLegitimateInterestSection.rangeSection.rangeEntries',
                fields: [
                    {
                        name: 'isARange',
                        bits: '1',
                        type: 'boolean'
                    },
                    {
                        name: 'startOrOnlyVendorId',
                        bits: '16',
                        type: 'number'
                    },
                    {
                        name: 'endVendorId',
                        bits: '16',
                        type: 'number',
                        condition: {
                            field: 'isARange',
                            value: true
                        }
                    }
                ],
                condition: {
                    field: 'vendorLegitimateInterestSection.isRangeEnconding',
                    value: true
                }
            },
            {
                name: 'publisherRestrictionsSection.numPubRestrictions',
                bits: '12',
                type: 'number'
            }
        ]
    };

    /**
     * @see TCStringDecoder.decode.
     */
    public decode(tcString: string, obj: any): void {
        log.debug(`Decoding TCString...`);
        const bitsString:string = decodeBase64BigEndian(tcString);
        log.debug(`Loaded TCString bits: ${bitsString}`);
        let remainBits:string = bitsString;
        FieldDefTCStringDecoder.fields.core.forEach((fieldOrArray:IField|IFieldArray) => {

            if ('iterations' in fieldOrArray) {
                remainBits = this.processFieldArray(<IFieldArray>fieldOrArray, obj, remainBits)
            } else {
                remainBits = FieldDefTCStringDecoder.processField(<IField>fieldOrArray, obj, remainBits);
            }
        });
        log.debug(`TCString decoded.`);
    }

    /**
     * Procesa un campo de tipo array.
     * Necesita de la referencia al objeto que tiene que informar según
     * los atributos del campo y la cadena de bits restante sin procesar
     * de donde obtener la información.
     * @param fieldArray Objeto FieldArray que representa una iteración de campos.
     * @param obj Objeto con toda la información ya procesada y donde informar valores.
     * @param bits Cadena de bits restantes.
     */
    private processFieldArray(fieldArray:IFieldArray, obj:any, bits:string) : string
    {
        let remainBits:string = bits;

        if (FieldDefTCStringDecoder.evaluateField(fieldArray, obj)) {
            let iterations: number;
            if (fieldArray.iterations.indexOf('#') == 0) {
                iterations = parseInt(FieldDefTCStringDecoder.getRefFieldValue(fieldArray.iterations, obj));
            } else {
                iterations = parseInt(fieldArray.iterations);
            }
            const itemArray:any[] = [];
            for (let it: number = 0; it < iterations; it++) {
                const item:any = {};
                fieldArray.fields.forEach((field: IField) => {
                    remainBits = FieldDefTCStringDecoder.processField(field, item, remainBits);
                });
                itemArray.push(item);
            }

            FieldDefTCStringDecoder.setValue(fieldArray, obj, itemArray);
        }

        return remainBits;
    }

    /**
     * Procesa un objeto Field para informar un atributo del objeto.
     * Necesita de la referencia al objeto que tiene que informar según
     * los atributos del campo y la cadena de bits restante sin procesar
     * de donde obtener la información.
     * @param field Campo a procesar
     * @param obj Objeto a informar
     * @param bits Cadena de bits restantes.
     */
    private static processField(field:IField, obj:any, bits:string) : string
    {
        let remainBits:string = bits;

        if (FieldDefTCStringDecoder.evaluateField(field, obj)) {
            log.debug(`Decoding ${field.name}...`);
            let numBits: number = 0;
            if (field.bits.indexOf('#') == 0) {
                numBits = parseInt(FieldDefTCStringDecoder.getRefFieldValue(field.bits, obj));
            } else {
                numBits = parseInt(field.bits);
            }
            const chain: string = remainBits.substr(0, numBits);
            log.debug(`Field ${field.name} has ${numBits} bits - ${chain}`);
            remainBits = remainBits.substr(numBits);

            let value: any = null;
            switch (field.type) {
                case 'number':
                    value = parseInt(chain, 2);
                    break;
                case 'string':
                    const number1: number = parseInt(chain.substr(0, numBits / 2), 2);
                    const number2: number = parseInt(chain.substr(numBits / 2), 2);
                    const letter1: string = FieldDefTCStringDecoder.LETTERS.charAt(number1);
                    const letter2: string = FieldDefTCStringDecoder.LETTERS.charAt(number2);
                    value = `${letter1}${letter2}`;
                    break;
                case 'boolean':
                    value = chain == '1';
                    break;
                case 'bitmap':
                    const bitmap: any = {};
                    for (let bitIdx: number = 0; bitIdx < numBits; bitIdx++) {
                        bitmap[bitIdx + 1] = chain.charAt(bitIdx) == '1';
                    }
                    value = bitmap;
                    break;
            }
            FieldDefTCStringDecoder.setValue(field, obj, value);
            log.debug(`Field ${field.name} decoded.`);
        }

        return remainBits;
    }

    /**
     * Método que recupera el valor de un atributo dada su referencia.
     * Recupera el valor de un atributo dado un objeto de donde recuperarlo
     * a través de una cadena separada por puntos que determinan los
     * niveles del atributo y que empieza por #.
     * @param ref Cadena con la referencia del atributo
     * @param obj Objeto de donde recuperar el atributo
     */
    private static getRefFieldValue(ref:string, obj:any) : any
    {
        let value:any;
        const fieldBitsParts: string[] = ref.split('#')[1].split('.');
        switch (fieldBitsParts.length) {
            case 3:
                value = (obj as any)[fieldBitsParts[0]][fieldBitsParts[1]][fieldBitsParts[2]];
                break;
            case 2:
                value = (obj as any)[fieldBitsParts[0]][fieldBitsParts[1]];
                break;
            case 1:
                value = (obj as any)[fieldBitsParts[0]];
                break;
        }
        return value;
    }

    /**
     * Determina si se cumplen las condiciones para que se procese un Field.
     * @param field Campo a comprobar
     * @param obj Objeto con información procesada para determinar la evalucación.
     */
    private static evaluateField(field:IGenericField, obj:any) : boolean
    {
        let evaluate:boolean = true;
        if (field.condition) {
            let fieldValue:any = FieldDefTCStringDecoder.getRefFieldValue(`#${field.condition.field}`, obj);
            evaluate = fieldValue == field.condition.value;
        }
        return evaluate;
    }

    /**
     * Establece el valor de un campo.
     * @param field Objeto Field que determina el campo.
     * @param obj Objeto a informar
     * @param value Valor a informar.
     */
    private static setValue(field:IGenericField, obj:any, value:any) : void
    {
        if (value != null) {
            if (value instanceof Object) {
                log.debug(`TCString field ${field.name} has value:`, value);
            } else {
                log.debug(`TCString field ${field.name} has value ${value}`);
            }
            const fieldPath: string[] = field.name.split('.');
            switch (fieldPath.length) {
                case 3:
                    (obj as any)[fieldPath[0]][fieldPath[1]][fieldPath[2]] = value;
                    break;
                case 2:
                    (obj as any)[fieldPath[0]][fieldPath[1]] = value;
                    break;
                case 1:
                    (obj as any)[fieldPath[0]] = value;
                    break;
            }
        }
    }
}

export {FieldDefTCStringDecoder}
